// ============================================================================
// SIGN-OUT SCOPE — the canonical contract for "how much do we sign out?"
// ============================================================================
//
// CRITICAL: `supabase.auth.signOut()` defaults to `scope: 'global'` in auth-js
// (GoTrueClient.signOut(options = { scope: 'global' })). Global deletes EVERY
// session and revokes EVERY refresh token for the user — on every device, and
// for every OAuth client, including the Meridian MCP connections on other
// machines.
//
// That is never what a person means when they click "Sign out" in one browser.
// Observed in prod 2026-08-28: an OAuth login for the Claude Code MCP client at
// 13:29:19.497 was killed by a browser sign-out 300ms later at 13:29:19.794,
// leaving zero rows in `auth.sessions` for a user who had just signed in.
//
// So: every call site states its scope, and the default everywhere is DEVICE.
// The wider scopes exist, but only behind a deliberate choice in the Hub
// account UI (/account → Security → Devices & sessions).
//
// Build-enforced by `scripts/validate-signout-scope.mjs` (`validate:signout-scope`):
// a bare `auth.signOut()` with no scope fails the build.
// ============================================================================
/** The safe default. Named so a reader sees the intent, not a magic string. */
export const DEFAULT_SIGN_OUT_SCOPE = "device";
/**
 * Map our intent-named scope to the auth-js wire value. Centralized so the
 * "device means local, NOT global" translation exists in exactly one place.
 */
export function toGoTrueSignOutScope(scope) {
    switch (scope) {
        case "others":
            return "others";
        case "everywhere":
            return "global";
        case "device":
            return "local";
    }
}
/**
 * True when the scope ends the CURRENT session — i.e. when the caller should
 * also clear local storage/cookies and navigate away. `others` deliberately
 * does NOT: the user stays signed in here, that is the whole point of it.
 */
export function scopeEndsCurrentSession(scope) {
    return scope !== "others";
}
/** Human-facing labels for the account UI. Single source so copy can't drift. */
export const SIGN_OUT_SCOPE_LABELS = {
    device: "This device",
    others: "All other devices",
    everywhere: "Everywhere (including this device)",
};
